import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthProvider } from './context/AuthContext';
import { ToastProvider } from './components/Toast';
import ProtectedRoute from './components/ProtectedRoute';
import PortalLayout from './components/PortalLayout';
import SiteLayout from './components/SiteLayout';
import ScrollToTop from './components/ScrollToTop';

import Home from './pages/site/Home';
import ForFamilies from './pages/site/ForFamilies';
import ForNannies from './pages/site/ForNannies';
import About from './pages/site/About';
import Locations from './pages/site/Locations';
import LocationRegion from './pages/site/LocationRegion';
import Faq from './pages/site/Faq';
import Jobs from './pages/site/Jobs';
import JobDetail from './pages/site/JobDetail';
import Contact from './pages/site/Contact';
import NotFound from './pages/site/NotFound';

import Account from './pages/Account';
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';

import NannyDashboard from './pages/nanny/NannyDashboard';
import NannyApplication from './pages/nanny/NannyApplication';
import NannyDocuments from './pages/nanny/NannyDocuments';
import NannyBookings from './pages/nanny/NannyBookings';
import NannyLeaveRequests from './pages/nanny/NannyLeaveRequests';
import NannyMessages from './pages/nanny/NannyMessages';
import NannyReviews from './pages/nanny/NannyReviews';

import FamilyDashboard from './pages/family/FamilyDashboard';
import FamilyProfile from './pages/family/FamilyProfile';
import FamilyRequest from './pages/family/FamilyRequest';
import FamilyBookings from './pages/family/FamilyBookings';
import FamilyMessages from './pages/family/FamilyMessages';
import NannyProfile from './pages/family/NannyProfile';

import './App.css';
import './site.css';

export default function App() {
  const { t } = useTranslation();

  const NANNY_LINKS = [
    { to: '/nanny', label: t('portal.nannyLinks.dashboard'), icon: '⌂', end: true },
    { to: '/nanny/application', label: t('portal.nannyLinks.application'), icon: '✎' },
    { to: '/nanny/documents', label: t('portal.nannyLinks.documents'), icon: '⎘' },
    { to: '/nanny/bookings', label: t('portal.nannyLinks.bookings'), icon: '☷' },
    { to: '/nanny/leave', label: t('portal.nannyLinks.leave'), icon: '☼' },
    { to: '/nanny/reviews', label: t('portal.nannyLinks.reviews'), icon: '★' },
    { to: '/nanny/messages', label: t('portal.nannyLinks.messages'), icon: '✉' },
    { to: '/nanny/account', label: t('portal.nannyLinks.account'), icon: '⚙' },
  ];

  const FAMILY_LINKS = [
    { to: '/family', label: t('portal.familyLinks.dashboard'), icon: '⌂', end: true },
    { to: '/family/request', label: t('portal.familyLinks.request'), icon: '✚' },
    { to: '/family/bookings', label: t('portal.familyLinks.bookings'), icon: '☷' },
    { to: '/family/profile', label: t('portal.familyLinks.profile'), icon: '✎' },
    { to: '/family/messages', label: t('portal.familyLinks.messages'), icon: '✉' },
    { to: '/family/account', label: t('portal.familyLinks.account'), icon: '⚙' },
  ];

  return (
    <AuthProvider>
      <ToastProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          {/* Public website */}
          <Route element={<SiteLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/for-families" element={<ForFamilies />} />
            <Route path="/for-nannies" element={<ForNannies />} />
            <Route path="/about" element={<About />} />
            <Route path="/locations" element={<Locations />} />
            <Route path="/locations/:region" element={<LocationRegion />} />
            <Route path="/jobs" element={<Jobs />} />
            <Route path="/jobs/:id" element={<JobDetail />} />
            <Route path="/faq" element={<Faq />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="*" element={<NotFound />} />
          </Route>

          {/* Sign in and registration */}
          <Route path="/login" element={<Login />} />
          <Route path="/register/nanny" element={<Register role="nanny" />} />
          <Route path="/register/family" element={<Register role="family" />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Nanny portal */}
          <Route
            path="/nanny"
            element={
              <ProtectedRoute role="nanny">
                <PortalLayout links={NANNY_LINKS} />
              </ProtectedRoute>
            }
          >
            <Route index element={<NannyDashboard />} />
            <Route path="application" element={<NannyApplication />} />
            <Route path="documents" element={<NannyDocuments />} />
            <Route path="bookings" element={<NannyBookings />} />
            <Route path="leave" element={<NannyLeaveRequests />} />
            <Route path="reviews" element={<NannyReviews />} />
            <Route path="messages" element={<NannyMessages />} />
            <Route path="account" element={<Account />} />
          </Route>

          {/* Family portal */}
          <Route
            path="/family"
            element={
              <ProtectedRoute role="family">
                <PortalLayout links={FAMILY_LINKS} />
              </ProtectedRoute>
            }
          >
            <Route index element={<FamilyDashboard />} />
            <Route path="request" element={<FamilyRequest />} />
            <Route path="bookings" element={<FamilyBookings />} />
            <Route path="messages" element={<FamilyMessages />} />
            <Route path="profile" element={<FamilyProfile />} />
            <Route path="nannies/:id" element={<NannyProfile />} />
            <Route path="account" element={<Account />} />
          </Route>
        </Routes>
      </BrowserRouter>
      </ToastProvider>
    </AuthProvider>
  );
}
